import { ResponsiveContainer } from '@arpeggi/ui'
import { observer } from 'mobx-react-lite'
import { FC, useEffect, useState } from 'react'
import { Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom'
import { styled } from '../../lib/stitches'
import { Box } from '../core/Components'
import { gray1 } from '../platform/color'
import { TopRightProfileCard } from '../platform/dashboard/ProfileCard'
import Tabs from '../platform/new-explore/components/Tabs'
import { SearchContext } from '../platform/new-explore/hooks/useSearch'
import FavoritedTable from './FavoritedTable'
import useProfileBannerColor from './hooks/useProfileBannerColor'
import MintsInProgressTable from './MintsInProgressTable'
import ProfileBanner from './ProfileBanner'
import ProfileInfo from './ProfileInfo'
import SongsTable from './SongsTable'
import SoundsTable from './SoundsTable'

const CRITERIA_WALLET_ADDRESS = '0x6458F00305F2E580001b5cd770B08B24B7e6785E'
// using this since we need an address with no mints, and it's possible
// we could mint a sound on behalf of someone to the null address
const ALMOST_NULL_ADDRESS = '0x0000000000000000000000000000000000000001'

const ProfileContainer = styled(Box, {
  backgroundColor: gray1,
  fontFamily: 'DM Sans'
})

const useProfileParams = () => useParams()

const ProfilePageContent: FC = observer(() => {
  const navigate = useNavigate()
  const { wallet_address, tab } = useProfileParams()
  const search = useState<string>()
  const bannerColor = useProfileBannerColor(wallet_address)

  const isCriteriaProfile = wallet_address === CRITERIA_WALLET_ADDRESS

  return (
    <ProfileContainer>
      <TopRightProfileCard />
      <ProfileBanner color={bannerColor} />
      <ResponsiveContainer css={{ position: 'relative' }}>
        <ProfileInfo />
      </ResponsiveContainer>
      <ResponsiveContainer>
        <MintsInProgressTable walletAddress={wallet_address} />
        <SearchContext.Provider value={search}>
          <Tabs.Root
            value={tab}
            onValueChange={(newval) => navigate(`/profile/${wallet_address}/${newval}`)}
          >
            <Box css={{ borderBottom: '1px solid rgba(0,0,0,.1)' }}>
              <Tabs.List css={{ maxWidth: '300px' }}>
                <Tabs.Trigger value={'sounds'}>Sounds</Tabs.Trigger>
                <Tabs.Trigger value={'songs'}>Songs</Tabs.Trigger>
                <Tabs.Trigger value={'favorited'}>Favorited</Tabs.Trigger>
              </Tabs.List>
            </Box>
            <Tabs.Content value={'sounds'}>
              <SoundsTable
                walletAddress={isCriteriaProfile ? ALMOST_NULL_ADDRESS : wallet_address}
              />
            </Tabs.Content>
            <Tabs.Content value={'songs'}>
              <SongsTable walletAddress={wallet_address} />
            </Tabs.Content>
            <Tabs.Content value={'favorited'}>
              <FavoritedTable walletAddress={wallet_address} />
            </Tabs.Content>
          </Tabs.Root>
        </SearchContext.Provider>
      </ResponsiveContainer>
    </ProfileContainer>
  )
})

const ProfilePage: FC = () => {
  const { wallet_address } = useProfileParams()
  const location = useLocation()
  const navigate = useNavigate()
  useEffect(() => {
    const { pathname } = location
    if (
      pathname === `/profile/${wallet_address}/created/stems` ||
      pathname === `/profile/${wallet_address}/created/samples`
    )
      return navigate(`/profile/${wallet_address}/sounds`, { replace: true })
    if (
      pathname === `/profile/${wallet_address}/created/songs` ||
      pathname === `/profile/${wallet_address}` ||
      pathname === `/profile/${wallet_address}/` ||
      pathname.includes(`/profile/${wallet_address}/created/`)
    )
      return navigate(`/profile/${wallet_address}/songs`, { replace: true })
    if (pathname.includes(`/profile/${wallet_address}/favorited/`))
      navigate(`/profile/${wallet_address}/favorited`, { replace: true })
  }, [location.pathname])
  return (
    <Routes>
      <Route path=":tab" element={<ProfilePageContent />} />
    </Routes>
  )
}

export default ProfilePage
